import { useState } from 'react';
import type { Word } from '../../types/word.types';
import { Button } from '../ui/button';
import { Trash2, Star } from 'lucide-react';
import { PartOfSpeechBadge } from './PartOfSpeechBadge';
import StarRating from './StarRating';
import { cn } from '../../lib/utils';

interface WordListItemListProps {
  word: Word;
  onDelete: (wordId: string) => void;
  onRatingChange?: (wordId: string, rating: number) => void;
}

function WordListItemList({ word, onDelete, onRatingChange }: WordListItemListProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleDelete = () => {
    onDelete(word.id);
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleRatingChange = (rating: number) => {
    onRatingChange?.(word.id, rating);
  };

  return (
    <div className={cn(
      "border rounded-lg bg-card transition-all duration-200",
      isExpanded && "ring-1 ring-primary/20 shadow-sm"
    )}>
      {/* Main Row */}
      <div className="flex items-center justify-between p-4 hover:bg-accent/30 transition-colors cursor-pointer"
           onClick={handleToggleExpand}>
        {/* Word */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold truncate text-foreground">
              {word.word}
            </h3>
            {/* Star Rating Display */}
            <div className="flex items-center gap-1 opacity-75">
              {Array.from({ length: 5 }, (_, i) => (
                <Star
                  key={i}
                  className={cn(
                    "h-3 w-3",
                    i < (word.rating || 1)
                      ? "fill-yellow-500 text-yellow-500"
                      : "text-muted-foreground/50"
                  )}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Delete Button */}
        <div className="flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
            aria-label={`Delete ${word.word}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Expandable Definition Section */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t bg-muted/20">
          <div className="pt-3 space-y-3">
            {/* Confidence Rating Section */}
            {onRatingChange && (
              <div className="pb-3 border-b border-border/50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">
                    Your confidence level:
                  </span>
                  <div className="flex items-center gap-2">
                    <StarRating
                      rating={word.rating || 1}
                      onRatingChange={handleRatingChange}
                      maxRating={5}
                    />
                  </div>
                </div>
              </div>
            )}

            {word.definitions.map((def, defIndex) => (
              <div key={defIndex} className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 bg-primary text-primary-foreground text-xs font-bold rounded-full">
                    {defIndex + 1}
                  </span>
                  <PartOfSpeechBadge partOfSpeech={def.partOfSpeech} />
                </div>

                <div className="ml-8 space-y-2">
                  {def.definitions?.map((subDef, subIndex) => (
                    <div key={subIndex} className="space-y-1">
                      <div className="border-l-2 border-primary/20 pl-3">
                        <p className="text-sm md:text-base text-foreground leading-relaxed">
                          {subDef.definition}
                        </p>
                      </div>

                      {subDef.example && (
                        <div className="ml-3 mt-2 p-2 bg-background rounded border-l-2 border-muted">
                          <p className="text-xs text-muted-foreground italic">
                            "{subDef.example}"
                          </p>
                        </div>
                      )}
                    </div>
                  )) || (
                    <div className="border-l-2 border-primary/20 pl-3">
                      <p className="text-sm md:text-base text-foreground leading-relaxed">
                        {def.meaning}
                      </p>
                      {def.example && (
                        <div className="mt-2 p-2 bg-background rounded border-l-2 border-muted">
                          <p className="text-xs text-muted-foreground italic">
                            "{def.example}"
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default WordListItemList;